import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@loot-os/ui/components/collapsible";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
} from "@loot-os/ui/components/sidebar";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useState } from "react";

/**
 * A sidebar row with pages folded beneath it: the row is its own link, the chevron beside it
 * folds and unfolds the list. The list opens whenever navigation lands on the row or one of its
 * pages (on load, or from a link elsewhere), so the section a page belongs to is visible;
 * folding it by hand sticks until the next arrival. Controlled, because the layout stays
 * mounted across routes and Base UI refuses a `defaultOpen` that changes after mount. Callers
 * render the `SidebarMenuSubItem`s.
 */
export function NavSubmenu({
  label,
  icon: Icon,
  active,
  link,
  badge,
  children,
}: {
  label: string;
  icon: LucideIcon;
  /** True while the row or one of its pages is the current page: highlights the row, unfolds the list. */
  active: boolean;
  /** The row's own link, rendered as the menu button. */
  link: ReactElement;
  /** Rendered after the label, before the chevron. */
  badge?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <SidebarMenuButton tooltip={label} isActive={active} render={link}>
          <Icon />
          <span className="truncate">{label}</span>
          {badge}
        </SidebarMenuButton>
        <CollapsibleTrigger render={<SidebarMenuAction />}>
          <ChevronRight className="transition-transform duration-200 group-data-open/collapsible:rotate-90" />
          <span className="sr-only">Toggle {label}</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>{children}</SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
